import React from 'react';
import { Typography, Container, Box, Paper, List, ListItem, Link, Fab } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const ScrollToTopButton = () => {
  const [visible, setVisible] = React.useState(false);
  const [fade, setFade] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 200) {
        setVisible(true);
      } else {
        setVisible(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setFade(true);
    setTimeout(() => {
      setVisible(false);
      setFade(false);
    }, 500); // duration matches the CSS transition
  };

  if (!visible) return null;

  return (
    <Box
      onClick={handleClick}
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 1000,
        opacity: fade ? 0 : 1,
        transition: 'opacity 0.5s ease-out',
      }}
    >
      <Fab color="primary" aria-label="scroll back to top">
        <KeyboardArrowUpIcon />
      </Fab>
    </Box>
  );
};

const PrivacyPolicy = () => {
  return (
    <Container maxWidth="md">
      <Paper sx={{ p: 4, my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" paragraph>
          Last updated: {new Date().toLocaleDateString()}
        </Typography>

        {/* Table of Contents */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h6" gutterBottom>
            Table of Contents
          </Typography>
          <List>
            <ListItem>
              <Link href="#information" underline="hover">
                1. Information We Collect
              </Link>
            </ListItem>
            <ListItem>
              <Link href="#usage" underline="hover">
                2. How We Use Your Information
              </Link>
            </ListItem>
            <ListItem>
              <Link href="#security" underline="hover">
                3. Data Security
              </Link>
            </ListItem>
            <ListItem>
              <Link href="#retention" underline="hover">
                4. Data Retention
              </Link>
            </ListItem>
            <ListItem>
              <Link href="#contact" underline="hover">
                5. Contact Us
              </Link>
            </ListItem>
          </List>
        </Box>

        <Box id="information" sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            1. Information We Collect
          </Typography>
          <Typography paragraph>
            We collect information that you provide directly to us when using our services. This includes, but is not limited to:
          </Typography>
          <ul>
            <li>Account details (name, email address, password)</li>
            <li>Reading progress and usage statistics to tailor your experience</li>
            <li>Payment information, processed securely through Stripe</li>
          </ul>
        </Box>

        <Box id="usage" sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            2. How We Use Your Information
          </Typography>
          <Typography paragraph>
            Your information is used to provide, maintain, and enhance our services. Specifically, we use your data to:
          </Typography>
          <ul>
            <li>Deliver and improve our speed reading training platform</li>
            <li>Personalize your experience and recommendations</li>
            <li>Process your subscriptions and transactions</li>
            <li>Send important notifications and updates about your account</li>
          </ul>
        </Box>

        <Box id="security" sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            3. Data Security
          </Typography>
          <Typography paragraph>
            We implement industry-standard security measures to safeguard your personal information. While no method of transmission over the Internet is 100% secure, we use encryption, access controls, and regular security reviews to protect your data.
          </Typography>
        </Box>

        <Box id="retention" sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            4. Data Retention
          </Typography>
          <Typography paragraph>
            We retain your information for as long as your account remains active or as necessary to provide you with our services. You may request data deletion by contacting us, and we will comply with applicable data protection laws.
          </Typography>
        </Box>

        <Box id="contact" sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            5. Contact Us
          </Typography>
          <Typography paragraph>
            If you have any questions regarding our privacy practices or wish to exercise your data rights, please contact us at{' '}
            <Link href="mailto:privacy@readerflex.com" underline="hover">
              privacy@readerflex.com
            </Link>.
          </Typography>
        </Box>
      </Paper>
      {/* Scroll-to-top button */}
      <ScrollToTopButton />
    </Container>
  );
};

export default PrivacyPolicy;
